import { useSyncExternalStore } from 'react';
import { classifyRelease, isLongPress, LONG_MS, MOVE_TOL, releaseVelocity, travel, Sample } from './gestures';

// One app-wide pointer tracker. Passive window listeners only; never preventDefault, so native
// scroll / text selection / existing drags are untouched. Toys subscribe via on(...).
export type PlayGestureType = 'tap' | 'flick' | 'longpress' | 'dragstart' | 'drag' | 'dragend' | 'move' | 'down' | 'up';
export type PointerKind = 'mouse' | 'touch' | 'pen';
export type PlayGesture = { x: number; y: number; vx: number; vy: number; type: PointerKind; target: Element | null };
export type PointerSnapshot = Readonly<{ x: number; y: number; down: boolean; type: PointerKind }>;

const MAX_SAMPLES = 64;
const EMA_ALPHA = 0.35;

const state = {
  x: 0, y: 0, vx: 0, vy: 0, down: false, type: 'mouse' as PointerKind,
  downX: 0, downY: 0, downAt: 0, target: null as Element | null,
};

const handlers: Record<PlayGestureType, Set<(e: PlayGesture) => void>> = {
  tap: new Set(), flick: new Set(), longpress: new Set(), dragstart: new Set(), drag: new Set(),
  dragend: new Set(), move: new Set(), down: new Set(), up: new Set(),
};
const reactListeners = new Set<() => void>();

const samples: Sample[] = [];
let started = false;
let dragging = false;
let longFired = false;
let longTimer = 0;
let lastMoveT = 0;
let raf = 0;
let pointerDead = false;
let touchId = -1;
let snap: PointerSnapshot = Object.freeze({ x: 0, y: 0, down: false, type: 'mouse' as PointerKind });

const now = () => performance.now();

// Dev-only inspection hook for browser debugging (stripped from production builds).
const debugEvents: string[] = [];
function debugLog(kind: string) {
  debugEvents.push(kind);
  if (debugEvents.length > 200) debugEvents.shift();
}
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  (window as unknown as { __playDebug: unknown }).__playDebug = {
    state,
    events: debugEvents,
    flags: () => ({ pointerDead, touchId, dragging, down: state.down }),
  };
}
const kindOf = (t: string): PointerKind => (t === 'touch' || t === 'pen' ? t : 'mouse');

function emit(kind: PlayGestureType, vx = state.vx, vy = state.vy) {
  if (process.env.NODE_ENV !== 'production') debugLog(kind);
  const e: PlayGesture = { x: state.x, y: state.y, vx, vy, type: state.type, target: state.target };
  handlers[kind].forEach((cb) => {
    try { cb(e); } catch (err) { if (process.env.NODE_ENV !== 'production') console.error('[mania/play]', err); }
  });
}

function refreshSnapshot() {
  if (snap.x !== state.x || snap.y !== state.y || snap.down !== state.down || snap.type !== state.type) {
    snap = Object.freeze({ x: state.x, y: state.y, down: state.down, type: state.type });
    reactListeners.forEach((l) => l());
  }
}

// rAF-batched React snapshot only (no CSS vars on <html>: a per-frame root style write would
// invalidate the whole document's style every pointer frame, including during touch scroll).
function flush() {
  raf = 0;
  refreshSnapshot();
}

function schedule() { if (!raf) raf = requestAnimationFrame(flush); }

function clearLong() { if (longTimer) { window.clearTimeout(longTimer); longTimer = 0; } }

function pushSample(x: number, y: number, t: number) {
  if (samples.length >= MAX_SAMPLES) samples.splice(1, 1); // keep origin (index 0) for travel()
  samples.push({ x, y, t });
}

function onDown(e: PointerEvent) {
  if (e.isPrimary === false) return;
  clearLong();
  pointerDead = false;
  touchId = -1; // pointerdown precedes touchstart; a lost touchend must not leave a stale id
  samples.length = 0;
  dragging = false;
  longFired = false;
  const t = now();
  state.x = state.downX = e.clientX; state.y = state.downY = e.clientY;
  state.vx = 0; state.vy = 0; state.down = true; state.downAt = t; lastMoveT = t;
  state.type = kindOf(e.pointerType);
  state.target = e.target instanceof Element ? e.target : null;
  pushSample(e.clientX, e.clientY, t);
  longTimer = window.setTimeout(() => {
    longTimer = 0;
    if (state.down && isLongPress(samples, state.downAt, now())) { longFired = true; emit('longpress'); }
  }, LONG_MS);
  emit('down');
  schedule();
}

function onMove(e: PointerEvent) {
  if (e.isPrimary === false) return;
  if (pointerDead && state.down) return; // touch events own this gesture now
  moveTo(e.clientX, e.clientY, e.target);
}

function moveTo(cx: number, cy: number, tgt: EventTarget | null) {
  const t = now();
  const dt = Math.max(1, t - lastMoveT);
  const ivx = (cx - state.x) / dt, ivy = (cy - state.y) / dt;
  state.vx += EMA_ALPHA * (ivx - state.vx);
  state.vy += EMA_ALPHA * (ivy - state.vy);
  lastMoveT = t;
  state.x = cx; state.y = cy;
  if (state.down) {
    pushSample(cx, cy, t);
    if (travel(samples) >= MOVE_TOL) {
      clearLong();
      if (!dragging) { dragging = true; emit('dragstart'); }
      emit('drag');
    }
  } else {
    state.target = tgt instanceof Element ? tgt : null;
  }
  emit('move');
  schedule();
}

function onUp(e: PointerEvent) {
  if (e.isPrimary === false || !state.down) return;
  if (pointerDead) return; // wait for touchend
  finish(e.clientX, e.clientY);
}

function finish(cx: number, cy: number) {
  const t = now();
  pointerDead = false; touchId = -1;
  state.x = cx; state.y = cy;
  pushSample(cx, cy, t);
  clearLong();
  const rv = releaseVelocity(samples);
  const kind = classifyRelease(samples, state.downAt, t);
  state.down = false;
  if (!longFired) {
    if (kind === 'tap') emit('tap');
    else if (kind === 'flick') emit('flick', rv.vx, rv.vy);
  }
  if (dragging) emit('dragend', rv.vx, rv.vy);
  dragging = false;
  emit('up');
  schedule();
}

// A touch pan on a scrollable page fires pointercancel while touch events keep flowing; the
// gesture survives and touchmove/touchend take over (the pointer stream is dead).
function onCancel(e: PointerEvent) {
  if (e.isPrimary === false || !state.down) return;
  if (state.type === 'touch' && touchId !== -1) { pointerDead = true; return; }
  abort();
}

function abort() {
  pointerDead = false; touchId = -1;
  clearLong();
  state.down = false;
  if (dragging) emit('dragend', 0, 0);
  dragging = false;
  emit('up');
  schedule();
}

function findTouch(list: TouchList): Touch | null {
  for (let i = 0; i < list.length; i++) if (list[i].identifier === touchId) return list[i];
  return null;
}

function onTouchStart(e: TouchEvent) {
  if (touchId === -1 && e.touches.length === 1 && e.changedTouches.length > 0) touchId = e.changedTouches[0].identifier;
}

function onTouchMove(e: TouchEvent) {
  if (!pointerDead || !state.down) return;
  const tc = findTouch(e.changedTouches);
  if (tc) moveTo(tc.clientX, tc.clientY, state.target);
}

function onTouchEnd(e: TouchEvent) {
  const tc = findTouch(e.changedTouches);
  if (!tc) return;
  if (pointerDead && state.down) finish(tc.clientX, tc.clientY);
  else touchId = -1;
}

function onTouchCancel(e: TouchEvent) {
  if (!findTouch(e.changedTouches)) return;
  if (state.down && pointerDead) abort(); else touchId = -1;
}

function subscribe(l: () => void) {
  reactListeners.add(l);
  return () => { reactListeners.delete(l); };
}
const getSnapshot = () => snap;

const OPTS: AddEventListenerOptions = { passive: true };

export const pointerEngine = {
  state,
  start() {
    if (started || typeof window === 'undefined') return;
    started = true;
    window.addEventListener('pointerdown', onDown, OPTS);
    window.addEventListener('pointermove', onMove, OPTS);
    window.addEventListener('pointerup', onUp, OPTS);
    window.addEventListener('pointercancel', onCancel, OPTS);
    window.addEventListener('touchstart', onTouchStart, OPTS);
    window.addEventListener('touchmove', onTouchMove, OPTS);
    window.addEventListener('touchend', onTouchEnd, OPTS);
    window.addEventListener('touchcancel', onTouchCancel, OPTS);
  },
  stop() {
    if (!started) return;
    started = false;
    window.removeEventListener('pointerdown', onDown, OPTS);
    window.removeEventListener('pointermove', onMove, OPTS);
    window.removeEventListener('pointerup', onUp, OPTS);
    window.removeEventListener('pointercancel', onCancel, OPTS);
    window.removeEventListener('touchstart', onTouchStart, OPTS);
    window.removeEventListener('touchmove', onTouchMove, OPTS);
    window.removeEventListener('touchend', onTouchEnd, OPTS);
    window.removeEventListener('touchcancel', onTouchCancel, OPTS);
    pointerDead = false; touchId = -1;
    clearLong();
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
    if (dragging) emit('dragend', 0, 0);
    if (state.down) { state.down = false; emit('up'); }
    state.down = false; dragging = false; longFired = false; samples.length = 0;
    refreshSnapshot();
  },
  on(type: PlayGestureType, cb: (e: PlayGesture) => void): () => void {
    handlers[type].add(cb);
    return () => { handlers[type].delete(cb); };
  },
  subscribe,
  getSnapshot,
};

export function usePointerSnapshot(): PointerSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
